import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { DialogProvider } from '@/components/DialogProvider';
import { PageHeaderProvider, usePageHeaderState } from '@/components/PageHeader';
import { CommandPaletteProvider, useCommandPalette } from '@/components/CommandPalette';
import { useToast } from '@/components/ToastProvider';
import { Sidebar, Topbar } from '@/components/AppShell';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { ApiError, clearCsrfToken } from '@/lib/api';
import { posthog } from '@/lib/posthog';
import { meQuery } from '@/lib/me';
import { useViewport } from '@/lib/useViewport';
import { navTitle, navSubtitle } from '@/lib/nav';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: AppLayout,
});

const COLLAPSE_KEY = 'ops.sidebar.collapsed';

// Binds the standard Topbar to the current route (title), the page-published
// header (subtitle/actions), and the toast system (bell reaction). Search/⌘K is
// wired in Phase 3 (CommandPalette); until then onOpenCommand is omitted so the
// search affordance doesn't render as a dead control.
function TopbarBound({ showMenu, onMenu }: { showMenu?: boolean; onMenu?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { subtitle, actions } = usePageHeaderState();
  const { toast } = useToast();
  const { openCommand } = useCommandPalette();
  return (
    <Topbar
      title={navTitle(pathname)}
      subtitle={subtitle ?? navSubtitle(pathname)}
      actions={actions}
      showMenu={showMenu}
      onMenu={onMenu}
      onOpenCommand={openCommand}
      onBell={() =>
        toast({ kind: 'info', title: 'No new alerts', message: "You're all caught up." })
      }
    />
  );
}

function AppLayout() {
  const me = Route.useLoaderData();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const vp = useViewport();

  // Desktop collapse preference, persisted. Tablet always uses the rail; mobile
  // uses a drawer (so this preference only governs desktop).
  const [collapsedPref, setCollapsedPref] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Leaving mobile closes the drawer.
  useEffect(() => {
    if (vp !== 'mobile') setDrawerOpen(false);
  }, [vp]);

  function toggleCollapse() {
    setCollapsedPref((v) => {
      const next = !v;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* ignore storage failures (private mode etc.) */
      }
      return next;
    });
  }

  // Track the user id we last identified to. When it changes, clear any CSRF
  // token cached against the previous session so the token paired with the new
  // session cookie is fetched fresh.
  const lastIdentifiedID = useRef<string | null>(null);

  // Under impersonation me.id is the TARGET — analytics must keep attributing
  // the browser to the real admin, never write to the target's person profile.
  const analyticsID = me.impersonation?.impersonator_id ?? me.id;
  const analyticsEmail = me.impersonation?.impersonator_email ?? me.email;

  // Identify-then-capture order matters: if pageview fires before identify the
  // first event lands anonymous. Run both in the same effect so the initial
  // dashboard landing carries the correct distinct_id.
  useEffect(() => {
    if (lastIdentifiedID.current !== analyticsID) {
      if (lastIdentifiedID.current !== null) {
        clearCsrfToken();
      }
      posthog.identify?.(analyticsID, { email: analyticsEmail });
      lastIdentifiedID.current = analyticsID;
    }
    if (me.impersonation) {
      posthog.register?.({ impersonating: true, impersonated_user_id: me.id });
    } else {
      posthog.unregister?.('impersonating');
      posthog.unregister?.('impersonated_user_id');
    }
    posthog.capture?.('pms_page_view', { path: pathname });
  }, [analyticsID, analyticsEmail, me.impersonation, me.id, pathname]);

  if (vp === 'mobile') {
    return (
      <PageHeaderProvider>
      <CommandPaletteProvider>
      <DialogProvider>
        <div className="flex min-h-screen flex-col">
          <ImpersonationBanner />
          <TopbarBound showMenu onMenu={() => setDrawerOpen(true)} />
          {drawerOpen && (
            <div
              className="fixed inset-0 z-50 bg-[rgba(15,23,42,0.5)]"
              onClick={(e) => {
                if (e.target === e.currentTarget) setDrawerOpen(false);
              }}
            >
              <div className="h-full w-[264px] [animation:ops-drawer-in_.18s_ease]">
                <Sidebar
                  me={me}
                  pathname={pathname}
                  drawer
                  onCloseDrawer={() => setDrawerOpen(false)}
                />
              </div>
            </div>
          )}
          <main className="min-w-0 flex-1">
            <div className="mx-auto w-full max-w-[1440px] px-5 py-5">
              <Outlet />
            </div>
          </main>
        </div>
      </DialogProvider>
      </CommandPaletteProvider>
      </PageHeaderProvider>
    );
  }

  // Tablet forces the icon rail (no toggle); desktop respects the preference.
  const railCollapsed = vp === 'tablet' ? true : collapsedPref;
  return (
    <PageHeaderProvider>
      <CommandPaletteProvider>
      <DialogProvider>
        <div
          className={`grid min-h-screen ${
            railCollapsed ? 'grid-cols-[4rem_1fr]' : 'grid-cols-[16rem_1fr]'
          }`}
        >
          <Sidebar
            me={me}
            pathname={pathname}
            collapsed={railCollapsed}
            onToggleCollapse={vp === 'tablet' ? undefined : toggleCollapse}
          />
          <div className="flex min-w-0 flex-col">
            <ImpersonationBanner />
            <TopbarBound />
            <main className="min-w-0">
              <div className="mx-auto w-full max-w-[1440px] px-6 py-6">
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </DialogProvider>
      </CommandPaletteProvider>
    </PageHeaderProvider>
  );
}
